import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectDetailMenuSite } from '@/redux/meta/selector'
import { selectStoreOrder } from '@/redux/order/selector'
import { selectStoreItemAvailability } from '@/redux/storeItemAvailability/selector'
import { selectStore } from '@/redux/stores/selector'
import { isObjectEmpty } from '@/utils/object'
import { productStatus } from '@/utils/product'

interface StoreUnavailability {
  unavailableStores?: {
    [storeId: string]: {
      endDateTime: number | null
    }
  }
  unavailableForAllStores?: boolean
}

interface StoreItemAvailability {
  resourceId: string
  unavailableStores: {
    [storeId: string]: {
      endDateTime: number | null
    }
  }
}

export interface StoreItemAvailabilityResult {
  selectedStore?: Oms.Store | null
  isUnavailableAtSelectedStore: boolean
  isUnavailableAtAllStores: boolean
  isUnavailableAtSomeStores: boolean
  availableStores: Oms.Store[]
  isPickup: boolean
  unavailableStores: Oms.Store[]
  initialized: boolean
  isPermanentlyUnavailable: boolean
  isTemporarilyUnavailable: boolean
}

type StoreItemAvailabilityMap = Record<string, StoreUnavailability>

const getStoreAvailabilityStatus = (
  store: Oms.Store,
  categoryUnavailability: StoreUnavailability,
  productUnavailability: StoreUnavailability,
  pickupTime: Date | null
): {
  isAvailable: boolean
  isUnavailable: boolean
} => {
  const unavailableResource =
    categoryUnavailability.unavailableStores?.[store.id] ||
    productUnavailability.unavailableStores?.[store.id]

  if (!unavailableResource) return { isAvailable: true, isUnavailable: false }

  if (unavailableResource.endDateTime === null) {
    return { isAvailable: false, isUnavailable: true }
  }

  if (!pickupTime) {
    return { isAvailable: false, isUnavailable: true }
  }

  const isUnavailable =
    unavailableResource.endDateTime >= new Date(pickupTime).getTime()

  return { isAvailable: !isUnavailable, isUnavailable }
}

const useStoreItemAvailability = ({
  product,
  category,
}: {
  product: Oms.MenuItem
  category: Oms.MenuCategory
}): StoreItemAvailabilityResult => {
  const metaState = useSelector(selectDetailMenuSite)
  const { enableTimeLimitUnavailability } = metaState?.features || {}
  const storeOrder = useSelector(selectStoreOrder)
  const allStore = useSelector(selectStore) || []
  const storeItemAvailability = useSelector(selectStoreItemAvailability) || []

  const stores = Object.values(allStore.entities).filter((store) => store.open)

  const selectedStore =
    storeOrder.data?.defaultStoreId !== null
      ? null
      : allStore.entities[storeOrder.data?.storeId]

  const unavailableResources: Record<string, StoreItemAvailability> =
    storeItemAvailability
      .filter(
        (item) =>
          item.unavailableStores &&
          storeOrder.data?.storeId &&
          item.unavailableStores[storeOrder.data.storeId as string]
      )
      .reduce((acc, cur) => {
        acc[cur.resourceId] = cur
        return acc
      }, {} as Record<string, StoreItemAvailability>)

  const { isPickup } = storeOrder.data || {}

  const pickupTime = useMemo(() => {
    if (!storeOrder.data?.collectionDate || !storeOrder.data?.collectionTime) {
      return null
    }
    return new Date(
      `${storeOrder.data?.collectionDate} ${
        storeOrder.data?.collectionTime.split(' to ')[0]
      }`
    )
  }, [storeOrder.data?.collectionDate, storeOrder.data?.collectionTime])

  const initialized = !isObjectEmpty(storeOrder.data)

  const storeItemAvailabilityMap = storeItemAvailability.reduce((acc, item) => {
    acc[item.resourceId] = item as unknown as StoreUnavailability
    return acc
  }, {} as StoreItemAvailabilityMap)

  const { categoryStoreLevelUnavailability, productStoreLevelUnavailability } =
    useMemo(
      () => ({
        categoryStoreLevelUnavailability:
          storeItemAvailabilityMap[category.id] || {},
        productStoreLevelUnavailability:
          storeItemAvailabilityMap[product.id] || {},
      }),
      [storeItemAvailabilityMap, category.id, product.id]
    )

  const { availableStores, unavailableStores } = useMemo(() => {
    if (!enableTimeLimitUnavailability) {
      const available = stores.filter(
        (store) =>
          !categoryStoreLevelUnavailability.unavailableStores?.[store.id] &&
          !productStoreLevelUnavailability.unavailableStores?.[store.id]
      )

      const unavailable = stores.filter(
        (store) =>
          categoryStoreLevelUnavailability.unavailableStores?.[store.id] ||
          productStoreLevelUnavailability.unavailableStores?.[store.id]
      )

      return { availableStores: available, unavailableStores: unavailable }
    }

    const available: Oms.Store[] = []
    const unavailable: Oms.Store[] = []

    stores.forEach((store) => {
      const { isAvailable, isUnavailable } = getStoreAvailabilityStatus(
        store,
        categoryStoreLevelUnavailability,
        productStoreLevelUnavailability,
        pickupTime
      )

      if (isAvailable) available.push(store)
      if (isUnavailable) unavailable.push(store)
    })

    return { availableStores: available, unavailableStores: unavailable }
  }, [
    stores,
    categoryStoreLevelUnavailability,
    productStoreLevelUnavailability,
    pickupTime,
    enableTimeLimitUnavailability,
  ])

  const isUnavailableAtSelectedStore = useMemo(() => {
    if (!enableTimeLimitUnavailability) {
      return Boolean(
        unavailableResources[category.id] ||
          unavailableResources[product.id] ||
          categoryStoreLevelUnavailability.unavailableForAllStores ||
          productStoreLevelUnavailability.unavailableForAllStores
      )
    }

    if (!selectedStore || product.status !== productStatus.UNAVAILABLE) {
      return false
    }

    const { isUnavailable } = getStoreAvailabilityStatus(
      selectedStore,
      categoryStoreLevelUnavailability,
      productStoreLevelUnavailability,
      pickupTime
    )

    return Boolean(isUnavailable)
  }, [
    category.id,
    categoryStoreLevelUnavailability,
    enableTimeLimitUnavailability,
    pickupTime,
    product,
    productStoreLevelUnavailability,
    selectedStore,
    unavailableResources,
  ])

  const isUnavailableAtAllStores = unavailableStores.length >= stores.length
  const isUnavailableAtSomeStores =
    unavailableStores.length > 0 && availableStores.length > 0

  const isPermanentlyUnavailable = (() => {
    if (!enableTimeLimitUnavailability) {
      return Boolean(
        categoryStoreLevelUnavailability.unavailableForAllStores ||
          productStoreLevelUnavailability.unavailableForAllStores
      )
    }

    return stores.every((store) => {
      const categoryUnavailable =
        categoryStoreLevelUnavailability.unavailableStores?.[store.id]
      const productUnavailable =
        productStoreLevelUnavailability.unavailableStores?.[store.id]

      return (
        categoryUnavailable?.endDateTime === null ||
        productUnavailable?.endDateTime === null
      )
    })
  })()

  const isTemporarilyUnavailable = (() => {
    if (!enableTimeLimitUnavailability) return false

    return stores.every((store) => {
      const categoryUnavailable =
        categoryStoreLevelUnavailability.unavailableStores?.[store.id]
      const productUnavailable =
        productStoreLevelUnavailability.unavailableStores?.[store.id]

      return (
        typeof categoryUnavailable?.endDateTime === 'number' ||
        typeof productUnavailable?.endDateTime === 'number'
      )
    })
  })()

  return {
    selectedStore,
    isUnavailableAtSelectedStore,
    isUnavailableAtAllStores,
    isUnavailableAtSomeStores,
    availableStores,
    isPickup,
    unavailableStores,
    initialized,
    isPermanentlyUnavailable,
    isTemporarilyUnavailable,
  }
}

export default useStoreItemAvailability
